// Update routes to include AITIME provisioning routes
// src/routes.js

import React from 'react';

// Authentication
const Login = React.lazy(() => import('./views/auth/Login'));
const Register = React.lazy(() => import('./views/auth/Register'));

// Dashboard
const Dashboard = React.lazy(() => import('./views/dashboard/Dashboard'));

// Projects
const ProjectList = React.lazy(() => import('./views/projects/ProjectList'));
const ProjectDetail = React.lazy(() => import('./views/projects/ProjectDetail'));
const ProjectForm = React.lazy(() => import('./views/projects/ProjectForm'));

// Payments
const PaymentList = React.lazy(() => import('./views/payments/PaymentList'));
const PaymentDetail = React.lazy(() => import('./views/payments/PaymentDetail'));

// Admin
const RoleManagement = React.lazy(() => import('./views/admin/Roles'));
const UserManagement = React.lazy(() => import('./views/admin/Users'));
const PermissionManagement = React.lazy(() => import('./views/admin/Permissions'));
const RoleForm = React.lazy(() => import('./views/admin/RoleForm'));
const APITokens = React.lazy(() => import('./views/admin/APITokens'));

// Admin Settings
const ApplicationTypesList = React.lazy(() => import('./views/admin/ApplicationTypesList'));
const ImplementationMethodsList = React.lazy(() => import('./views/admin/ImplementationMethodsList'));
const PaymentMethodsList = React.lazy(() => import('./views/admin/PaymentMethodsList'));
const DistributorsList = React.lazy(() => import('./views/admin/DistributorsList'));

// Profile
const Profile = React.lazy(() => import('./views/profile/Profile'));

// Others
const UnauthorizedPage = React.lazy(() => import('./views/pages/Unauthorized'));
const ExpenseList = React.lazy(() => import('./views/expenses/ExpenseList'));
const ExpenseForm = React.lazy(() => import('./views/expenses/ExpenseForm'));
const ExpenseCategories = React.lazy(() => import('./views/expenses/ExpenseCategories'));
const ExpenseReports = React.lazy(() => import('./views/expenses/ExpenseReports'));

// Provisioning (both products)
const UnbilledProvisioningReport = React.lazy(() => import('./views/provisioning/UnbilledProvisioningReport'));

// GymViz Provisioning
const GymVizProvisioningList = React.lazy(() => import('./views/gymviz/GymVizProvisioningList'));
const GymVizProvisioningForm = React.lazy(() => import('./views/gymviz/GymVizProvisioningForm'));
const GymVizProvisioningDetail = React.lazy(() => import('./views/gymviz/GymVizProvisioningDetail'));

// AITIME Provisioning
const ProvisioningList = React.lazy(() => import('./views/aitime/ProvisioningList'));
const ProvisioningForm = React.lazy(() => import('./views/aitime/ProvisioningForm'));
const ProvisioningDetail = React.lazy(() => import('./views/aitime/ProvisioningDetail'));

// Targets
const BudgetList = React.lazy(() => import('./views/budgets/BudgetList'));

// Issues
const IssueList = React.lazy(() => import('./views/issues/IssueList'));
const IssueDetail = React.lazy(() => import('./views/issues/IssueDetail'));

// Customers
const CustomerList = React.lazy(() => import('./views/customers/CustomerList'));
const CustomerForm = React.lazy(() => import('./views/customers/CustomerForm'));
const CustomerDetail = React.lazy(() => import('./views/customers/CustomerDetail'));

// Leads
const LeadPipeline = React.lazy(() => import('./views/leads/LeadPipeline'));

// Reports
const SalesReports = React.lazy(() => import('./views/reports/SalesReports'));

const routes = [
  { path: '/', exact: true, name: 'Home' },
  
  // Public routes (no permission required)
  { path: '/login', name: 'Login', element: Login },
  { path: '/register', name: 'Register', element: Register },
  { path: '/unauthorized', name: 'Unauthorized', element: UnauthorizedPage },
  
  // Profile (any authenticated user)
  { path: '/profile', name: 'Profile', element: Profile },

  // Protected routes with permissions
  {
    path: '/dashboard', 
    name: 'Dashboard', 
    element: Dashboard,
    // Must match navigation.js's dashboard entry. Support holds no
    // view_projects, so with view_projects alone the menu offered the link
    // and this route answered Access Denied -- for the one role the server
    // builds a support preset for.
    requirePermission: ['view_projects', 'view_issues']
  },
  
  // Project routes
  { 
    path: '/projects', 
    name: 'Projects', 
    element: ProjectList, 
    exact: true,
    requirePermission: 'view_projects'
  },
  { 
    path: '/projects/new', 
    name: 'Create Project', 
    element: ProjectForm,
    requirePermission: 'create_project'
  },
  { 
    path: '/projects/:id', 
    name: 'Project Details', 
    element: ProjectDetail,
    requirePermission: 'view_projects'
  },
  { 
    path: '/projects/:id/edit', 
    name: 'Edit Project', 
    element: ProjectForm,
    requirePermission: 'edit_project'
  },
  
  // Payment routes
  {
    path: '/payments',
    name: 'Payments',
    element: PaymentList,
    requirePermission: 'view_payments'
  },
  {
    path: '/payments/:id',
    name: 'Payment Details',
    element: PaymentDetail,
    requirePermission: 'view_payments'
  },
  
  // Admin routes
  {
    path: '/admin/roles',
    name: 'Role Management',
    element: RoleManagement,
    requirePermission: 'manage_roles'
  },
  {
    path: '/admin/users',
    name: 'User Management',
    element: UserManagement,
    requirePermission: 'manage_users'
  },
  {
    path: '/admin/permissions',
    name: 'Permission Management',
    element: PermissionManagement,
    requirePermission: 'manage_roles'
  },
  {
    path: '/admin/roles/new',
    name: 'Add Role',
    element: RoleForm,
    requirePermission: 'manage_roles'
  },
  {
    path: '/admin/roles/edit/:id',
    name: 'Edit Role',
    element: RoleForm,
    requirePermission: 'manage_roles'
  },
  {
    path: '/admin/api-tokens',
    name: 'API Tokens',
    element: APITokens
  },
  
  // Admin Settings routes
  { 
    path: '/admin/application-types', 
    name: 'Application Types', 
    element: ApplicationTypesList,
    requirePermission: 'manage_application_types'
  },
  { 
    path: '/admin/implementation-methods', 
    name: 'Implementation Methods', 
    element: ImplementationMethodsList,
    requirePermission: 'manage_application_types'
  },
  {
    path: '/admin/payment-methods',
    name: 'Payment Methods',
    element: PaymentMethodsList,
    requirePermission: 'manage_application_types'
  },
  {
    path: '/admin/distributors',
    name: 'Distributors',
    element: DistributorsList,
    requirePermission: 'manage_distributors'
  },

  // Expense routes
  { 
    path: '/expenses', 
    name: 'Expenses', 
    element: ExpenseList, 
    exact: true,
    requirePermission: 'view_expenses'
  },
  {
    path: '/expenses/new',
    name: 'New Expense',
    element: ExpenseForm,
    requirePermission: 'add_expense'
  },
  {
    path: '/expenses/:id/edit',
    name: 'Edit Expense',
    element: ExpenseForm,
    requirePermission: 'edit_expense'
  },
  { 
    path: '/expense-categories', 
    name: 'Expense Categories', 
    element: ExpenseCategories,
    requirePermission: 'view_expenses'
  },
  { 
    path: '/expense-reports', 
    name: 'Expense Reports', 
    element: ExpenseReports,
    requirePermission: 'view_expenses'
  },
  
  // Provisionings with no project: company money, so gated on the financials
  // codename alone -- the Financial Manager who acts on it holds no
  // provisioning permission. Mirrors PMSBE/PMSBE/urls.py.
  {
    path: '/provisioning/unbilled',
    name: 'Running With No Project',
    element: UnbilledProvisioningReport,
    requirePermission: 'view_company_financials'
  },

  // GymViz Provisioning routes
  {
    path: '/provisioning/gymviz',
    name: 'GymViz Provisioning',
    element: GymVizProvisioningList,
    requirePermission: ['view_gymviz_provisioning', 'manage_gymviz_provisioning']
  },
  {
    path: '/provisioning/gymviz/new',
    name: 'New GymViz Provisioning',
    element: GymVizProvisioningForm,
    requirePermission: ['manage_gymviz_provisioning', 'start_demo_provisioning']
  },
  {
    path: '/provisioning/gymviz/:id/edit',
    name: 'Edit GymViz Provisioning',
    element: GymVizProvisioningForm,
    requirePermission: 'manage_gymviz_provisioning'
  },
  {
    path: '/provisioning/gymviz/:id',
    name: 'GymViz Provisioning Details',
    element: GymVizProvisioningDetail,
    requirePermission: ['view_gymviz_provisioning', 'manage_gymviz_provisioning']
  },

  // AITIME Provisioning routes
  {
    path: '/aitime/provisioning',
    name: 'AITIME Provisioning',
    element: ProvisioningList,
    requirePermission: ['view_aitime_provisioning', 'manage_aitime_provisioning']
  },
  {
    path: '/aitime/provisioning/new',
    name: 'New AITIME Provisioning',
    element: ProvisioningForm,
    requirePermission: ['manage_aitime_provisioning', 'start_demo_provisioning']
  },
  {
    path: '/aitime/provisioning/:id',
    name: 'Edit AITIME Provisioning',
    element: ProvisioningForm,
    requirePermission: 'manage_aitime_provisioning'
  },
  {
    path: '/aitime/provisioning/:id/view',
    name: 'AITIME Provisioning Details',
    element: ProvisioningDetail,
    requirePermission: ['view_aitime_provisioning', 'manage_aitime_provisioning']
  },

  // Target routes
  // Read on view_project_financials, because everyone who is MEASURED against
  // a target has to be able to see it -- the same gate as budgets/
  // permissions.py::CanViewTargets. What a reader without
  // view_company_financials must not see is the firm's margin and cost base,
  // and that is the serializer's field split, not a second route. Writing is
  // gated separately inside the screen on manage_budgets, which every holder
  // of it also carries -- so the dashboard meter's "Set a target" link cannot
  // land on Access Denied.
  {
    path: '/targets',
    name: 'Targets',
    element: BudgetList,
    exact: true,
    requirePermission: 'view_project_financials'
  },

  // Issue routes
  // Anyone holding a view codename sees every issue -- a deliberate departure
  // from project row scoping, because a support ticket arrives unassigned and
  // must be visible to whoever picks it up. Mirrors issues/permissions.py.
  {
    path: '/issues',
    name: 'Tickets',
    element: IssueList,
    exact: true,
    requirePermission: ['view_issues', 'create_issue', 'edit_issue']
  },
  {
    path: '/issues/:id',
    name: 'Ticket Details',
    element: IssueDetail,
    requirePermission: ['view_issues', 'create_issue', 'edit_issue']
  },

  // Customer routes
  // /customers and /customers/:id are widened to view_leads/manage_leads too
  // (fix round 2): a lead and a customer are the same row in one table, and
  // the lead board already shows those records/links to these routes, so a
  // user who may work leads must be able to open them. The backend agrees
  // (CustomerViewSet.retrieve -> CanViewCustomers, widened the same way).
  {
    path: '/customers',
    name: 'Customers',
    element: CustomerList,
    exact: true,
    requirePermission: ['view_customers', 'manage_customers', 'view_leads', 'manage_leads']
  },
  {
    path: '/customers/new',
    name: 'New Customer',
    element: CustomerForm,
    requirePermission: ['manage_customers', 'manage_leads']
  },
  {
    path: '/customers/:id',
    name: 'Customer Details',
    element: CustomerDetail,
    requirePermission: ['view_customers', 'manage_customers', 'view_leads', 'manage_leads']
  },
  {
    path: '/customers/:id/edit',
    name: 'Edit Customer',
    element: CustomerForm,
    requirePermission: 'manage_customers'
  },

  // Lead routes
  {
    path: '/leads',
    name: 'Lead Pipeline',
    element: LeadPipeline,
    requirePermission: ['view_leads', 'manage_leads']
  },

  // Reports routes
  {
    path: '/sales-reports',
    name: 'Sales Reports',
    element: SalesReports,
    requirePermission: 'view_sales_reports'
  }
];

export default routes;